import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../../lib/supabase';
import { useApi } from '../../lib/use-api';
import { VerifiedBadge } from '../../components/common/Badges';
import { ArrowLeft, Loader2, Radio, Trophy, CheckCircle2, Circle } from 'lucide-react';
import { useUnits, paceSecPerUnit, unitLabel } from '../../lib/units';

/**
 * EventLeaderboard — public, no-auth-required-to-watch live leaderboard
 * for a single event. Anyone (even non-joined users) can land here from
 * the EventDetail "Watch live" button.
 *
 * Two sections:
 *   • LIVE — runners currently in `state='live'` (still racing). Polled
 *           every 10 s so spectators see fresh data without a refresh.
 *   • FINISHED — verified finishers, ranked by finish_time_sec.
 *
 * When the race is over we hide LIVE and only show FINISHED.
 */
const POLL_MS = 10_000;

export default function EventLeaderboard() {
  const { id } = useParams();
  const nav = useNavigate();
  const { units } = useUnits();
  const { data: event } = useApi(api.getEvent, [id]);
  const { data: leaderboard, loading: lbLoading, refresh: refreshLb } = useApi(api.getLeaderboard, [id]);
  const { data: live, loading: liveLoading, refresh: refreshLive } = useApi(api.getLiveParticipants, [id]);
  const [tick, setTick] = useState(0);

  // Auto-poll while the race is live.
  useEffect(() => {
    if (!event || event.raceState !== 'live') return;
    const t = setInterval(() => {
      refreshLive();
      refreshLb();
      setTick((v) => v + 1);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [event?.raceState]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLive = event?.raceState === 'live';
  const liveRunners = (live || []).filter((r) => r.state === 'live');

  return (
    <div className="px-4 pt-4 pb-6 space-y-5" data-testid="event-leaderboard">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="lb-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <div className="flex items-center gap-2">
          <p className="rivl-label">Leaderboard</p>
          {isLive && (
            <span
              className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full bg-success text-white text-[9px] font-bold uppercase tracking-widest"
              data-testid="lb-live-badge"
            >
              <Radio className="h-2.5 w-2.5 animate-pulse" /> Live
            </span>
          )}
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
          {event?.name || 'Event'}
        </h1>
        {isLive && (
          <p className="text-[11px] text-muted-foreground mt-1">Updates every 10 s · tick {tick + 1}</p>
        )}
      </div>

      {/* Live section — only when the race is currently in progress */}
      {isLive && (
        <section data-testid="lb-live-section">
          <div className="flex items-center justify-between mb-2">
            <h2 className="rivl-label">On course</h2>
            <span className="text-[11px] text-muted-foreground">{liveRunners.length} running</span>
          </div>
          {liveLoading && !live ? (
            <Loader />
          ) : liveRunners.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No runners on course yet.</p>
          ) : (
            <div className="rivl-card divide-y divide-border" data-testid="lb-live-table">
              {liveRunners.map((r) => (
                <div key={r.userId} className="flex items-center gap-3 px-4 py-3">
                  <Circle className="h-2.5 w-2.5 text-success fill-success animate-pulse flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.name}</div>
                    <div className="font-mono text-[11px] text-muted-foreground truncate">
                      {r.bib ? `Bib ${r.bib} · ` : ''}@{r.handle}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-xs text-success font-bold uppercase tracking-widest">LIVE</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Finishers — always shown */}
      <section data-testid="lb-finished-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="rivl-label flex items-center gap-1.5">
            <Trophy className="h-3.5 w-3.5" /> {isLive ? 'Finished so far' : 'Final results'}
          </h2>
        </div>
        {lbLoading && !leaderboard ? (
          <Loader />
        ) : (leaderboard || []).length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No verified finishers yet.</p>
        ) : (
          <div className="rivl-card divide-y divide-border" data-testid="lb-finished-table">
            {(leaderboard || []).map((row, i) => {
              const paceSec = row.paceSecPerKm ? Math.round(paceSecPerUnit(row.paceSecPerKm, units)) : null;
              const paceLabel = paceSec ? `${Math.floor(paceSec / 60)}:${String(paceSec % 60).padStart(2, '0')}/${unitLabel(units)}` : row.pace;
              return (
                <div key={`${row.rank}-${i}`} className={`flex items-center gap-3 px-4 py-3 ${row.isYou ? 'bg-primary/5' : ''}`}>
                  <div className="font-mono font-bold w-8 text-primary">#{row.rank}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium flex items-center gap-2 truncate">
                      {row.name}
                      {row.isYou && <span className="text-[9px] uppercase tracking-widest text-primary">YOU</span>}
                    </div>
                    <div className="font-mono text-[11px] text-muted-foreground">
                      {row.bib ? `Bib ${row.bib} · ` : ''}{paceLabel}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold tabular-nums">{row.time}</div>
                    {row.verified && <VerifiedBadge />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <div className="pt-2">
        <Link to={`/events/${id}`} className="text-xs text-primary font-medium flex items-center gap-1" data-testid="lb-event-detail-link">
          <CheckCircle2 className="h-3 w-3" /> Event details
        </Link>
      </div>
    </div>
  );
}

function Loader() {
  return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
}
